import { createApolloProvider } from "@vue/apollo-option";
import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
} from "@apollo/client/core";
import { graphQlUrl } from "@/core/helpers/constant";
import JwtService, {
  REFRESH_TOKEN_KEY,
  TOKEN_KEY,
} from "@/core/services/JwtService";
import REFRESH_TOKEN from "@/graphql/auth/RefreshToken.graphql";
import { AuthInterface } from "@/store/auth/interfaces/AuthInterface";
import { onError } from "@apollo/client/link/error";

/**
 * @description enable data in-memory cache for apollo provider
 */
const appApolloCache = new InMemoryCache();
const logApolloCache = new InMemoryCache();

/**
 * @description main app graphql and log endpoint
 */
const endpointUrl = `${graphQlUrl}/graphql` || "http://localhost:4000/graphql";
const logEndpointUrl =
  `${graphQlUrl}/graphql` || "http://localhost:4000/graphql";

/**
 * @description main app graphql endpoint root (without /graphql)
 */
export const filesRoot = graphQlUrl || "http://localhost:4000";

let isAlreadyFetchingAccessToken = true;

// /**
//  * @description create new apollo link for handler token and refresh token
//  */
// const apolloLink = new ApolloLink((operation, forward) => {
//   operation.setContext(({ headers }) => ({
//     headers: {
//       ...headers,
//       authorization: JwtService.getToken()
//         ? `Bearer ${JwtService.getToken()}`
//         : null,
//     },
//   }));
//
//   return forward(operation); // Go to the next link in the chain. Similar to `next` in Express.js middleware.
// });

const endpointLink = new HttpLink({
  uri: endpointUrl,
});

const logLink = new HttpLink({
  uri: endpointUrl,
});

const middlewareLink = new ApolloLink((operation, forward) => {
  const oldHeaders = operation.getContext().headers;

  operation.setContext({
    headers: {
      ...oldHeaders,
      authorization: JwtService.getToken(),
    },
  });
  return forward(operation);
});

/**
 * @description customize apollo on error event for handler
 * token and refresh token
 */
const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(async function (graphQLError) {
        switch (graphQLError && graphQLError.extensions["code"]) {
          // Apollo Server sets code to UNAUTHENTICATED
          // when an AuthenticationError is thrown in a resolver
          case "UNAUTHENTICATED": {
            // Modify the operation context with a new token
            // error code is set to UNAUTHENTICATED
            // when AuthenticationError thrown in resolver
            if (!isAlreadyFetchingAccessToken) {
              isAlreadyFetchingAccessToken = true;

              try {
                const { data } = await appApolloClient.mutate({
                  mutation: REFRESH_TOKEN,
                  variables: {
                    refreshToken: REFRESH_TOKEN_KEY,
                  },
                });

                isAlreadyFetchingAccessToken = false;

                // Update accessToken in localStorage
                JwtService.saveToken(data.refreshToken.accessToken);
                JwtService.saveRefreshToken(data.refreshToken.refreshToken);

                const oldHeaders = operation.getContext().headers;

                operation.setContext({
                  headers: {
                    ...oldHeaders,
                    authorization: JwtService.getToken(),
                  },
                });

                // Retry the request, returning the new observable
                return forward(operation);
              } catch (error) {
                console.log(error);
                JwtService.destroyToken();
              }
            }

            break;
          }
        }
      });
    }

    // To retry on network errors, we recommend the RetryLink
    // instead of the onError link. This just logs the error.
    if (networkError) {
      console.log(`[Network error]: ${networkError}`);
    }
  }
);

/**
 * @description create apollo client for main app endpoint
 */
const appApolloClient = new ApolloClient({
  cache: appApolloCache,
  uri: endpointUrl,
  link: ApolloLink.from([errorLink, middlewareLink, endpointLink]),
});

/**
 * @description create apollo client for log endpoint
 */
const logApolloClient = new ApolloClient({
  cache: logApolloCache,
  uri: logEndpointUrl,
  link: ApolloLink.from([errorLink, middlewareLink, logLink]),
});

/**
 * @description create apollo provider
 */
const provider = createApolloProvider({
  // Multiple clients support
  // Use the 'client' option inside queries
  // or '$client' on the apollo definition
  clients: {
    appApiClient: appApolloClient,
    logApiClient: logApolloClient,
  },
  // Default client
  defaultClient: appApolloClient,
  // Default 'apollo' definition
  defaultOptions: {
    // See 'apollo' definition
    // For example: default query options
    $query: {
      loadingKey: "loading",
      fetchPolicy: "cache-and-network",
    },
    // LocalStorage token
    tokenName: TOKEN_KEY,
  },
  // Watch loading state for all queries
  // See 'Smart Query > options > watchLoading' for detail
  // watchLoading(isLoading, countModifier) {
  //   loading += countModifier;
  //   console.log("Global loading", loading, countModifier);
  // },
  // Global error handler for all smart queries and subscriptions

  errorHandler(error) {
    console.log("Global error handler");
    console.error(error);
  },
  // Globally turn off prefetch ssr
  prefetch: false,
});

/**
 * @description manually call this when user logging in
 * @param apolloClient
 * @param userId
 * @param auth
 */
export async function onLogin(
  apolloClient,
  userId: string,
  auth: AuthInterface
) {
  if (typeof localStorage !== "undefined" && auth.token && auth.refreshToken) {
    JwtService.saveUid(userId);
    JwtService.saveToken(auth.token);
    JwtService.saveRefreshToken(auth.refreshToken);
  }

  try {
    await apolloClient.resetStore();
  } catch (e) {
    console.log("%cError on cache reset (login)", "color: orange;", e);
  }
}

/**
 * @description manually call this when user logging out
 * @param apolloClient
 */
export async function onLogout(apolloClient) {
  if (typeof localStorage !== "undefined") {
    JwtService.destroyToken();
  }

  try {
    await apolloClient.cache.reset();
    await apolloClient.clearStore();
    await apolloClient.resetStore();
  } catch (e) {
    console.log("%cError on cache reset (logout)", "color: orange;", e);
  }
}

export const apolloProvider = provider;
export const appApiClient = provider.clients.appApiClient;
export const logApiClient = provider.clients.logApiClient;
